import * as React from 'react'

interface ContextRingProps {
  inputTokens?: number
  contextWindow?: number
  className?: string
}

function formatTokenCount(tokens: number): string {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(tokens >= 10000 ? 0 : 1)}k`
  return tokens.toString()
}

function getUsageColor(percent: number): string {
  if (percent >= 95) return 'var(--destructive)'
  if (percent >= 80) return 'var(--warning)'
  if (percent >= 50) return 'var(--info)'
  return 'var(--muted-foreground)'
}

export function calcPercent(inputTokens: number, contextWindow: number): number {
  if (contextWindow <= 0) return 0
  return Math.max(0, Math.min(100, (inputTokens / contextWindow) * 100))
}

export { formatTokenCount, getUsageColor }

export function ContextRing({ inputTokens = 0, contextWindow = 0, className }: ContextRingProps) {
  const percent = calcPercent(inputTokens, contextWindow)

  const size = 16
  const strokeWidth = 2
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (percent / 100) * circumference
  const color = getUsageColor(percent)

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`block ${className ?? ''}`}
      aria-hidden="true"
    >
      {/* Background track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--muted-foreground)"
        strokeWidth={strokeWidth}
        opacity={0.15}
      />
      {/* Progress ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease' }}
      />
    </svg>
  )
}
